import { useTranslation } from 'react-i18next'

import {
  DWELLER_USER_ROLE,
  LANDLORD_USER_ROLE,
} from '../../../../../constants/user'
import { useDateLocale, useTimeZone, useUserData } from '../../../../../hooks'
import { isStaffUser } from '../../../../../utils/user'
import { getEntityActionTypeSelectOptionsEditOnly } from '../../../../Entity'
import {
  getUserEditRoleFormModalInitialValues,
  getUserEditTextFieldFormModalInitialValues,
  getUserProfileHeaderInfoItemActions,
  USER_FORM_EXTERNAL_ID_FIELD_MAX_LENGTH,
  UserEditRoleFormModal,
  UserEditTextFieldFormModal,
  useUserProfileContext,
  useUserProfileHeaderEditSubmit,
  useUserProfileHeaderInfoItemsProps,
} from '../../../../User'

import {
  USER_PROFILE_PAGE_HEADER_INHABITANT_EDIT_EXTERNAL_ID_FIELD_NAME,
  USER_PROFILE_PAGE_HEADER_INHABITANT_EDIT_EXTERNAL_ID_MODAL_QA_NAME,
  USER_PROFILE_PAGE_HEADER_INHABITANT_INFO_ITEM_ACTION_QA_NAMES,
  USER_PROFILE_PAGE_HEADER_INHABITANT_INFO_ITEMS_LOADING_ITEMS_LENGTH,
  UserProfilePageHeaderInhabitantInfoItemName,
} from './useUserProfilePageHeaderInhabitantInfoItems.constants'
import {
  getUserProfilePageHeaderInhabitantEditRolePayload,
  getUserProfilePageHeaderInhabitantInfoItems,
} from './useUserProfilePageHeaderInhabitantInfoItems.utils'

export function useUserProfilePageHeaderInhabitantInfoItems() {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()

  const { handleCloseModal, setModal, userData } = useUserProfileContext()

  const currentUser = useUserData()

  const { handleEditSubmit } = useUserProfileHeaderEditSubmit()

  // Dwellers and landlords never work with the external id, so the item is
  // shown to staff viewers only.
  const infoItems = getUserProfilePageHeaderInhabitantInfoItems({
    canViewExternalId: isStaffUser(currentUser),
    locale,
    t,
    timezone,
    userData,
  })

  const editOnlyTypeSelectProps = {
    disabled: true,
    options: getEntityActionTypeSelectOptionsEditOnly(),
  }

  function handleOpenEditRoleModal() {
    setModal(
      <UserEditRoleFormModal
        formFieldsProps={{ typeSelectProps: editOnlyTypeSelectProps }}
        formTitle='Modal.ChangeRoleTitleKey'
        getPayload={getUserProfilePageHeaderInhabitantEditRolePayload}
        initialValues={getUserEditRoleFormModalInitialValues({
          role: userData?.isLandlord ? LANDLORD_USER_ROLE : DWELLER_USER_ROLE,
          t,
        })}
        onClose={handleCloseModal}
        onSubmit={handleEditSubmit}
      />
    )
  }

  function handleOpenEditExternalIdModal() {
    setModal(
      <UserEditTextFieldFormModal
        fieldLabelKey='User.ExternalIdLabelKey'
        fieldMaxLength={USER_FORM_EXTERNAL_ID_FIELD_MAX_LENGTH}
        fieldName={
          USER_PROFILE_PAGE_HEADER_INHABITANT_EDIT_EXTERNAL_ID_FIELD_NAME
        }
        fieldPlaceholderKey='User.ExternalIdPlaceholderKey'
        formFieldsProps={{ typeSelectProps: editOnlyTypeSelectProps }}
        formTitle='UserProfile.EditExternalIdModalTitleKey'
        initialValues={getUserEditTextFieldFormModalInitialValues({
          fieldName:
            USER_PROFILE_PAGE_HEADER_INHABITANT_EDIT_EXTERNAL_ID_FIELD_NAME,
          value: userData?.externalId,
        })}
        key={USER_PROFILE_PAGE_HEADER_INHABITANT_EDIT_EXTERNAL_ID_FIELD_NAME}
        qaName={
          USER_PROFILE_PAGE_HEADER_INHABITANT_EDIT_EXTERNAL_ID_MODAL_QA_NAME
        }
        onClose={handleCloseModal}
        onSubmit={handleEditSubmit}
      />
    )
  }

  const infoItemsHandlers = {
    [UserProfilePageHeaderInhabitantInfoItemName.ExternalId]:
      getUserProfileHeaderInfoItemActions(handleOpenEditExternalIdModal),
    [UserProfilePageHeaderInhabitantInfoItemName.Role]:
      getUserProfileHeaderInfoItemActions(handleOpenEditRoleModal),
  }

  return useUserProfileHeaderInfoItemsProps({
    actionQaNames:
      USER_PROFILE_PAGE_HEADER_INHABITANT_INFO_ITEM_ACTION_QA_NAMES,
    handlers: infoItemsHandlers,
    items: infoItems,
    loadingItemsLength:
      USER_PROFILE_PAGE_HEADER_INHABITANT_INFO_ITEMS_LOADING_ITEMS_LENGTH,
  })
}
